<script setup lang="ts">
  import { useLinkTarget } from '@/shared/composables/useLinkTarget'
  import ButtonQC from '@qicgroupdoha/qic-connect-ds/components/ButtonQC'
  interface Props {
    isMultiple?: boolean
    name?: string
    policyLink?: string
    receiptLink?: string
    inboundPolicyLink?: string
    inboundReceiptLink?: string
  }
  interface Emits {
    (e: 'downloadReceipt'): void
    (e: 'downloadPolicy'): void
    (e: 'downloadInboundPolicy'): void
    (e: 'downloadInboundReceipt'): void
  }
  withDefaults(defineProps<Props>(), {
    isMultiple: false,
    name: '',
    policyLink: '',
    receiptLink: '',
    inboundPolicyLink: '',
    inboundReceiptLink: '',
  })
  const emit = defineEmits<Emits>()
  const { getLinkTarget } = useLinkTarget()
</script>

<template>
  <div class="inbound-policies" :class="{ 'inbound-policies--multiple': isMultiple }">
    <div class="inbound-policies__wrapper">
      <div class="inbound-policies__header">
        <p class="inbound-policies__header-title text-medium">
          {{ $t('visitors.success_page.health_block.header') }}
        </p>
        <span class="inbound-policies__header-annotation text-small-regular">{{
          isMultiple
            ? $t('visitors.success_page.health_block.several_visitors_annotation')
            : $t('qic_insuranse_success_annotation')
        }}</span>
      </div>

      <div class="inbound-policies__buttons">
        <ButtonQC
          wide
          variant="outline"
          size="medium"
          icon="file-download"
          data-test="download-policy-button"
          :href="policyLink"
          :target="getLinkTarget()"
          @click="emit('downloadPolicy')"
        >
          {{
            isMultiple
              ? $t('visitors.success_page.download_buttons.all_policies')
              : $t('visitors.success_page.download_buttons.policy')
          }}
        </ButtonQC>
        <ButtonQC
          wide
          variant="outline"
          size="medium"
          icon="file-download"
          data-test="download-receipt-button"
          :href="receiptLink"
          :target="getLinkTarget()"
          @click="emit('downloadReceipt')"
        >
          {{
            isMultiple
              ? $t('visitors.success_page.download_buttons.all_receipts')
              : $t('visitors.success_page.download_buttons.receipt')
          }}
        </ButtonQC>
      </div>
    </div>

    <div class="inbound-policies__wrapper">
      <div class="inbound-policies__header">
        <p class="inbound-policies__header-title text-medium">
          {{ $t('visitors.success_page.trip_block.header') }}
        </p>
        <span class="inbound-policies__header-name text-small-regular">{{ name }}</span>
      </div>

      <div class="inbound-policies__buttons">
        <ButtonQC
          wide
          variant="outline"
          size="medium"
          icon="file-download"
          data-test="download-inbound-policy-button"
          :href="inboundPolicyLink"
          :target="getLinkTarget()"
          @click="emit('downloadInboundPolicy')"
        >
          {{ $t('visitors.success_page.download_buttons.policy') }}
        </ButtonQC>
        <ButtonQC
          wide
          variant="outline"
          size="medium"
          icon="file-download"
          data-test="download-inbound-receipt-button"
          :href="inboundReceiptLink"
          :target="getLinkTarget()"
          @click="emit('downloadInboundReceipt')"
        >
          {{ $t('visitors.success_page.download_buttons.receipt') }}
        </ButtonQC>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .inbound-policies {
    display: flex;
    padding: var(--spacing-7);
    flex-direction: column;
    align-self: stretch;
    border-radius: var(--radius-7);
    background: var(--colorBgSrfSecondaryAlpha);

    @media (max-width: 568px) {
      padding: var(--spacing-6);
    }

    a {
      width: 100%;
    }
  }

  .inbound-policies__wrapper {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-5);
    &:first-child {
      padding-bottom: var(--spacing-7);
      border-bottom: 1px solid var(--colorBrdrSrfTertiary);
    }
    &:last-child {
      padding-top: var(--spacing-7);
    }
  }

  .inbound-policies__header {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-1);
  }

  .inbound-policies__buttons {
    display: flex;
    gap: var(--spacing-3);
    @media (max-width: 568px) {
      flex-direction: column;
    }
  }

  .inbound-policies--multiple {
    .inbound-policies__buttons {
      flex-direction: column !important;
    }
  }
</style>
